import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Truck, CheckCircle, Camera, QrCode } from 'lucide-react';
import {
  useDispatchTransfer,
  useVerifyDispatchItem,
  useStartDispatchVerification,
  type DispatchTransferInput,
} from '../../../api/mutations/transfer.mutations';
import type { Transfer } from '../../../api/queries/transfer.queries';

interface DispatchModalProps {
  transfer: Transfer;
  onClose: () => void;
  onSuccess: () => void;
}

interface VerificationItem {
  itemId: string;
  ingredientName: string;
  quantity: number;
  unit: string;
  batchNumber?: string;
  isVerified: boolean;
  scannedBarcode?: string;
}

export default function DispatchModal({ transfer, onClose, onSuccess }: DispatchModalProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<VerificationItem[]>(
    transfer.items.map((item) => {
      const ingredient = typeof item.ingredientId === 'object' ? item.ingredientId : null;
      return {
        itemId: item._id,
        ingredientName: ingredient?.name?.en || '-',
        quantity: item.approvedQuantity || item.requestedQuantity,
        unit: item.unit,
        batchNumber: item.batchNumber,
        isVerified: item.dispatchVerified || false,
        scannedBarcode: item.dispatchScanData?.scannedBarcode,
      };
    })
  );
  const [dispatchPhotos, _setDispatchPhotos] = useState<string[]>(transfer.dispatchPhotos || []);
  const [notes, setNotes] = useState('');
  const [skipVerification, setSkipVerification] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [manualBarcodeInput, setManualBarcodeInput] = useState('');
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);

  const startVerification = useStartDispatchVerification(transfer._id);
  const verifyItem = useVerifyDispatchItem(transfer._id);
  const dispatchTransfer = useDispatchTransfer(transfer._id);

  const allVerified = items.every((item) => item.isVerified);
  const verifiedCount = items.filter((item) => item.isVerified).length;

  const handleStartVerification = async () => {
    try {
      await startVerification.mutateAsync();
    } catch (error) {
      console.error('Failed to start verification:', error);
    }
  };

  const handleVerifyItem = async (index: number, barcode: string, scanType: 'supplier_barcode' | 'internal_qr' | 'manual') => {
    const item = items[index];
    try {
      const result = await verifyItem.mutateAsync({
        itemId: item.itemId,
        scannedBarcode: barcode,
        scanType,
      });

      if (result.itemVerified) {
        setItems(items.map((it, i) =>
          i === index ? { ...it, isVerified: true, scannedBarcode: barcode } : it
        ));
      }
      setManualBarcodeInput('');
      setSelectedItemIndex(null);
    } catch (error) {
      console.error('Failed to verify item:', error);
    }
  };

  const handleManualVerify = (index: number) => {
    if (!manualBarcodeInput.trim()) {
      alert(t('transfers.enterBarcode'));
      return;
    }
    handleVerifyItem(index, manualBarcodeInput, 'manual');
  };

  const handleDispatch = async () => {
    if (!allVerified && !skipVerification) {
      alert(t('transfers.verifyAllItems'));
      return;
    }

    if (skipVerification && !skipReason.trim()) {
      alert(t('transfers.skipReasonRequired'));
      return;
    }

    try {
      const payload: DispatchTransferInput = {
        dispatchPhotos: dispatchPhotos.length > 0 ? dispatchPhotos : undefined,
        notes: notes || undefined,
        skipVerification,
        skipReason: skipVerification ? skipReason : undefined,
      };
      await dispatchTransfer.mutateAsync(payload);
      onSuccess();
    } catch (error) {
      console.error('Failed to dispatch transfer:', error);
    }
  };

  const fromLocation = typeof transfer.fromLocationId === 'object' ? transfer.fromLocationId : null;
  const toLocation = typeof transfer.toLocationId === 'object' ? transfer.toLocationId : null;

  const needsVerificationStart = transfer.dispatchVerificationRequired &&
    transfer.status === 'approved' &&
    !transfer.dispatchFullyVerified;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            <h2 className="text-xl font-bold">{t('transfers.dispatchTransfer')}</h2>
            <span className="text-sm text-muted-foreground">({transfer.transferNumber})</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Transfer Info */}
            <div className="p-4 rounded-lg bg-secondary/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">{t('transfers.from')}</p>
                  <p className="font-medium">{fromLocation?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('transfers.to')}</p>
                  <p className="font-medium">{toLocation?.name || '-'}</p>
                </div>
              </div>
            </div>

            {/* Verification Progress */}
            {transfer.dispatchVerificationRequired && (
              <div className="flex items-center gap-4 p-4 rounded-lg border">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{t('transfers.verificationProgress')}</span>
                    <span className="text-sm text-muted-foreground">
                      {verifiedCount}/{items.length} {t('transfers.verified')}
                    </span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${allVerified ? 'bg-green-500' : 'bg-primary'}`}
                      style={{ width: `${(verifiedCount / items.length) * 100}%` }}
                    />
                  </div>
                </div>
                {allVerified && (
                  <CheckCircle className="h-6 w-6 text-green-500" />
                )}
              </div>
            )}

            {/* Start Verification Button */}
            {needsVerificationStart && (
              <button
                onClick={handleStartVerification}
                disabled={startVerification.isPending}
                className="w-full py-3 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center justify-center gap-2"
              >
                <QrCode className="h-5 w-5" />
                {startVerification.isPending ? t('common.loading') : t('transfers.startVerification')}
              </button>
            )}

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('transfers.itemsToDispatch')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('transfers.ingredient')}</th>
                      <th className="text-right py-2 px-3">{t('transfers.quantity')}</th>
                      <th className="text-left py-2 px-3">{t('transfers.batch')}</th>
                      <th className="text-center py-2 px-3 w-32">{t('transfers.verified')}</th>
                      {transfer.dispatchVerificationRequired && (
                        <th className="text-center py-2 px-3 w-32">{t('transfers.action')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.itemId} className={`border-t ${item.isVerified ? 'bg-green-50' : ''}`}>
                        <td className="py-2 px-3">{item.ingredientName}</td>
                        <td className="py-2 px-3 text-right">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {item.batchNumber || '-'}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {item.isVerified ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              {t('common.yes')}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{t('common.no')}</span>
                          )}
                        </td>
                        {transfer.dispatchVerificationRequired && (
                          <td className="py-2 px-3 text-center">
                            {!item.isVerified && (
                              <button
                                onClick={() => setSelectedItemIndex(index)}
                                className="px-2 py-1 rounded bg-primary text-primary-foreground text-xs hover:bg-primary/90"
                              >
                                {t('transfers.verify')}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Manual Barcode Input */}
            {selectedItemIndex !== null && (
              <div className="p-4 rounded-lg border border-primary bg-primary/5">
                <h4 className="text-sm font-medium mb-2">
                  {t('transfers.verifyItem')}: {items[selectedItemIndex].ingredientName}
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={manualBarcodeInput}
                    onChange={(e) => setManualBarcodeInput(e.target.value)}
                    placeholder={t('transfers.enterBarcode')}
                    className="flex-1 rounded-lg border bg-background px-3 py-2"
                    autoFocus
                  />
                  <button
                    onClick={() => handleManualVerify(selectedItemIndex)}
                    disabled={verifyItem.isPending}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {verifyItem.isPending ? t('common.loading') : t('transfers.verify')}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedItemIndex(null);
                      setManualBarcodeInput('');
                    }}
                    className="px-4 py-2 rounded-lg border hover:bg-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Skip Verification Option */}
            {transfer.dispatchVerificationRequired && !allVerified && (
              <div className="p-4 rounded-lg border border-yellow-200 bg-yellow-50">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={skipVerification}
                    onChange={(e) => setSkipVerification(e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      {t('transfers.skipVerification')}
                    </p>
                    <p className="text-xs text-yellow-700">{t('transfers.skipVerificationWarning')}</p>
                  </div>
                </label>
                {skipVerification && (
                  <textarea
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    className="w-full mt-3 rounded-lg border bg-background px-3 py-2"
                    rows={2}
                    placeholder={t('transfers.skipReasonPlaceholder')}
                    required
                  />
                )}
              </div>
            )}

            {/* Dispatch Photos */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('transfers.dispatchPhotos')}
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors flex items-center gap-2"
                  onClick={() => {
                    // In a real implementation, this would open a file picker or camera
                    alert(t('transfers.photoUploadNotImplemented'));
                  }}
                >
                  <Camera className="h-4 w-4" />
                  {t('transfers.addPhoto')}
                </button>
                <span className="text-sm text-muted-foreground">
                  {dispatchPhotos.length} {t('transfers.photosAdded')}
                </span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
                placeholder={t('transfers.dispatchNotesPlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleDispatch}
            disabled={
              dispatchTransfer.isPending ||
              (!allVerified && !skipVerification) ||
              (skipVerification && !skipReason.trim())
            }
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Truck className="h-4 w-4" />
            {dispatchTransfer.isPending ? t('common.saving') : t('transfers.confirmDispatch')}
          </button>
        </div>
      </div>
    </div>
  );
}
